export function parseJwt(token: string) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join('')
  )

  return JSON.parse(jsonPayload)
}

export const getNow = () => Date.now()

export const getDelayBeforeExpiration = (token: string) => {
  const { exp } = parseJwt(token)
  return Math.trunc(exp * 1000 - getNow())
}

/*
   True only when a token is gone or its exp has passed — i.e. when the only way
   back is a full reload to re-bootstrap auth. A still-valid token can be
   recovered by an in-place reconnect, so it returns false. Fake/dev tokens are
   treated as non-expiring; unparseable tokens are treated conservatively (see inline).
*/
export const isTokenExpired = (token: string | null | undefined): boolean => {
  if (!token) return true
  if (token.startsWith('FAKE_TOKEN:')) return false
  try {
    return getDelayBeforeExpiration(token) <= 0
  } catch {
    return true // can't read exp — don't trust it, force re-bootstrap
  }
}

// oidc-client-ts persists the signed-in user under the `oidc.` key prefix.
export const clearStoredOidcUser = () => {
  Object.keys(window.localStorage)
    .filter((key) => key.startsWith('oidc.'))
    .forEach((key) => window.localStorage.removeItem(key))
}

interface StoredOidcUser {
  access_token?: string
  expires_at?: number
}

// oidc-client-ts persists the signed-in user as JSON under the `oidc.user:` key
const getStoredOidcUser = (): StoredOidcUser | null => {
  try {
    const key = Object.keys(window.localStorage).find((k) =>
      k.startsWith('oidc.user:')
    )
    return key ? (JSON.parse(window.localStorage[key]) as StoredOidcUser) : null
  } catch {
    return null
  }
}

/*
   The OpenFin WindowFrame renders outside the auth gate with no auth in its
   store, so it judges sign-in from the persisted OIDC user. Check the token is
   still valid, not merely present: a lingering-but-expired user would otherwise
   leave a stale titlebar showing while the view falls back to the sign-in page.
*/
export const hasValidStoredOidcUser = (): boolean => {
  const user = getStoredOidcUser()
  return Boolean(user?.expires_at && user.expires_at * 1000 > Date.now())
}

// name comes from the access token's `name` claim, same derivation as OidcWrapper
export const getStoredOidcUserName = (): string => {
  const token = getStoredOidcUser()?.access_token
  try {
    return token ? parseJwt(token).name ?? '' : ''
  } catch {
    return ''
  }
}
